import { ref, computed } from 'vue';
import axios from 'axios';
import { platformFilterToApi } from '../utils/matchPlatform.js';

// Stats where lower values are better (matches DESCENDING_STAT_COLUMNS in Rails)
const DESCENDING_STATS = new Set([
  'time_to_kill_average', 'time_to_damage_average',
  'crosshair_placement_average', 'initial_crosshair_placement_average',
  'trade_kill_fails', 'traded_death_fails',
  'util_he_friend_damage', 'util_flash_friends_flashed',
  'util_flash_total_friend_duration', 'util_lost_cost', 'deaths',
]);

// Friendly map names
const MAP_NAMES = {
  de_dust2: 'Dust II',
  de_mirage: 'Mirage',
  de_inferno: 'Inferno',
  de_nuke: 'Nuke',
  de_overpass: 'Overpass',
  de_ancient: 'Ancient',
  de_anubis: 'Anubis',
  de_vertigo: 'Vertigo',
  de_train: 'Train',
  de_mills: 'Mills',
  de_edin: 'Edin',
};

/**
 * Maps backend unit type symbols (time, integer, percentage, decimal) to
 * the frontend format strings the formatters expect.
 */
function mapUnitType(backendType) {
  const mapping = {
    time: 'seconds',
    integer: 'integer',
    percentage: 'percentage',
    decimal: 'decimal',
    degrees: 'degrees',
  };
  return mapping[backendType] || backendType || 'decimal';
}

/**
 * Determines the display unit type for a stat based on its name.
 * Mirrors classify_stat_unit in the Rails ClickHouse model.
 * Used as fallback when the backend doesn't provide unit_type.
 */
function getUnitType(name) {
  if (/crosshair_placement_average/.test(name)) return 'degrees';
  if (/^time_to_|_time$|_duration$/.test(name)) return 'seconds';
  if (/_(rate|percentage|accuracy)$/.test(name) || name === 'kast') return 'percentage';
  // Known decimal stats (ratings, kdr, kpr, adr, impact, damage_per_*)
  const decimalStats = new Set([
    'kdr', 'kpr', 'adr', 'hltv_rating', 'refrag_rating', 'impact',
    'damage_per_shot', 'damage_per_hit',
  ]);
  if (decimalStats.has(name)) return 'decimal';
  return 'integer';
}

/**
 * Transforms flat backend averages into stat objects the UI components expect.
 * Each stat becomes { players_value, rating_range_average_value, deviations_away, unit_type, is_ascending, recommended_mods }
 */
function buildStatObjects(averages) {
  if (!averages) return {};
  const stats = {};
  for (const [key, value] of Object.entries(averages)) {
    stats[key] = {
      players_value: value,
      rating_range_average_value: null,
      deviations_away: null,
      unit_type: getUnitType(key),
      is_ascending: !DESCENDING_STATS.has(key),
      recommended_mods: [],
    };
  }
  return stats;
}

/**
 * Flattens a backend match entry ({ match, overview, outcome }) into the
 * flat shape that MatchRow and chart code expect.
 */
// Stats stored in 0-100 range in ClickHouse that need normalization to 0-1
const PERCENTAGE_100_KEYS = new Set(['kast']);

function flattenMatchEntry(entry) {
  const m = entry.match;
  const o = { ...entry.overview };

  // Normalize 0-100 percentage stats to 0-1
  for (const key of PERCENTAGE_100_KEYS) {
    if (o[key] != null) o[key] = o[key] / 100;
  }

  const outcomeToResult = { won: 1, lost: 0, tied: 2 };
  return {
    ...o,
    match_id: m.uuid,
    uuid: m.uuid,
    map_name: m.map_name,
    platform: m.platform || m.match_platform,
    match_type: m.match_type,
    match_platform: m.match_platform,
    lan_party: m.lan_party || null,
    played_on: m.played_on,
    date: m.played_on,
    status: m.status,
    match_result: outcomeToResult[entry.outcome] ?? null,
    match_score: m.a_score != null ? `${m.a_score}-${m.b_score}` : null,
    a_score: m.a_score,
    b_score: m.b_score,
    end_rank: o.end_rank,
    start_rank: o.start_rank ?? o.start_Rank,
    kills_per_round: o.kpr,
    headshot_kill_percentage: o.headshot_kills != null && o.kills
      ? o.headshot_kills / (o.kills || 1)
      : o.headshot_kill_percentage ?? 0,
  };
}

/**
 * Computes per-stat improvement between two halves of a points array.
 * Returns an object keyed by stat name with { recent_value, previous_value, is_ascending, unit_type }.
 */
function computeProgressFromPoints(points) {
  if (!points || points.length < 2) return {};

  const mid = Math.ceil(points.length / 2);
  const recent = points.slice(mid);
  const previous = points.slice(0, mid);

  if (recent.length === 0 || previous.length === 0) return {};

  const sampleOverview = points[0]?.overview;
  if (!sampleOverview) return {};

  const numericKeys = Object.keys(sampleOverview).filter(
    (k) => typeof sampleOverview[k] === 'number' && k !== 'steam_id' && k !== 'end_rank' && k !== 'start_Rank' && k !== 'start_rank',
  );

  const progress = {};
  for (const key of numericKeys) {
    const recentAvg = recent.reduce((s, p) => s + (p.overview[key] || 0), 0) / recent.length;
    const previousAvg = previous.reduce((s, p) => s + (p.overview[key] || 0), 0) / previous.length;
    progress[key] = {
      recent_value: recentAvg,
      previous_value: previousAvg,
      is_ascending: !DESCENDING_STATS.has(key),
      unit_type: getUnitType(key),
    };
  }
  return progress;
}

/**
 * Normalizes map stats entries from the backend into the shape the UI expects.
 */
function normalizeMapEntry(entry) {
  return {
    ...entry,
    map_name_friendly: MAP_NAMES[entry.map_name] || entry.map_name?.replace('de_', '').replace(/^\w/, (c) => c.toUpperCase()),
    match_count: entry.matches_played,
    win_rate: entry.winrate,
  };
}

/**
 * Main composable for all coach profile data.
 */
export function useCoachData(steamId, filters) {
  // Profile state
  const profileStats = ref(null);
  const extraStats = ref(null);
  const weaponStats = ref(null);
  const profileLoading = ref(false);
  const profileError = ref(null);

  // Match history state
  const matchHistory = ref(null);
  const matchHistoryLoading = ref(false);
  const totalMatchCount = ref(0);
  const totalPageCount = ref(0);
  const currentPage = ref(1);

  // Progress state
  const progressData = ref(null);

  // Map stats state
  const mapStats = ref(null);

  function buildParams() {
    return {
      platform: platformFilterToApi(filters.platform.value),
      match_type: filters.matchType.value.toLowerCase(),
      map_name: filters.map.value?.name?.toLowerCase() || '',
    };
  }

  async function fetchProfile() {
    profileLoading.value = true;
    profileError.value = null;
    try {
      const { data } = await axios.get(`/coach/players/${steamId.value}/profile`, {
        params: buildParams(),
      });

      // Backend returns enriched averages (objects with players_value, peer data, etc.)
      // or flat numbers if peer data isn't available. Handle both.
      const avg = data.averages || {};
      const firstVal = Object.values(avg)[0];
      if (firstVal && typeof firstVal === 'object' && 'players_value' in firstVal) {
        // Enriched format — use directly, just normalize unit_type from symbol to string
        const stats = {};
        for (const [key, stat] of Object.entries(avg)) {
          stats[key] = {
            ...stat,
            unit_type: mapUnitType(stat.unit_type),
          };
        }
        profileStats.value = stats;
      } else {
        // Flat format fallback — wrap in stat objects
        profileStats.value = buildStatObjects(avg);
      }

      extraStats.value = data.extra || { match_count: data.matches_played, win_rate: 0 };
      weaponStats.value = data.weapon_stats || null;
    } catch (err) {
      profileError.value = err;
      profileStats.value = null;
    } finally {
      profileLoading.value = false;
    }
  }

  async function fetchMatchHistory(page = 1) {
    matchHistoryLoading.value = true;
    try {
      const { data } = await axios.get(`/coach/players/${steamId.value}/recent_match_stats`, {
        params: { ...buildParams(), page },
      });
      matchHistory.value = (data.matches || []).map(flattenMatchEntry);
      totalMatchCount.value = data.pagination?.total_count || 0;
      totalPageCount.value = data.pagination?.total_pages || 0;
      currentPage.value = data.pagination?.page || page;
    } catch {
      matchHistory.value = [];
      totalMatchCount.value = 0;
      totalPageCount.value = 0;
    } finally {
      matchHistoryLoading.value = false;
    }
  }

  async function fetchRecentProgress() {
    try {
      const { data } = await axios.get(`/coach/players/${steamId.value}/recent_progress`, {
        params: buildParams(),
      });
      progressData.value = computeProgressFromPoints(data.points || []);
    } catch {
      progressData.value = null;
    }
  }

  async function fetchMapStats() {
    try {
      const { map_name, ...params } = buildParams();
      const { data } = await axios.get(`/coach/players/${steamId.value}/map_stats`, { params });
      mapStats.value = {
        maps: (data.maps || []).map(normalizeMapEntry),
      };
    } catch {
      mapStats.value = null;
    }
  }

  function fetchAll() {
    fetchProfile();
    fetchMatchHistory(1);
    fetchRecentProgress();
    fetchMapStats();
  }

  // Recent stats (win/loss/tie from current page)
  const recentStats = computed(() => {
    if (!matchHistory.value?.length) return null;
    const total = matchHistory.value.length;
    const wins = matchHistory.value.filter((m) => m.match_result === 1).length;
    return {
      win_rate: ((wins / total) * 100).toFixed(0),
    };
  });

  return {
    // Profile
    profileStats,
    extraStats,
    weaponStats,
    profileLoading,
    profileError,
    fetchProfile,

    // Match history
    matchHistory,
    matchHistoryLoading,
    totalMatchCount,
    totalPageCount,
    currentPage,
    fetchMatchHistory,

    // Progress
    progressData,
    fetchRecentProgress,

    // Map stats
    mapStats,
    fetchMapStats,

    // Convenience
    recentStats,
    fetchAll,
  };
}

export {
  getUnitType, mapUnitType, DESCENDING_STATS, MAP_NAMES, flattenMatchEntry, buildStatObjects,
};
